import { useState, useRef, useEffect } from "react";
import arrowRight from "../../../Assets/images/arrow-right.svg";
import { resendEmail, verifyUpdatedEmail } from "../../../Services/settingsServices";
import Loader from "react-js-loader";

function EmailSettingsScreen3({ onVerifyEmail }) {
  const updatedEmail = sessionStorage.getItem("updatedEmail");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCount, setResendCount] = useState(10);

  const length = 6;
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const inputRefs = useRef([]);

  const resendFunction = () => {
    for (let i = 10; i > 0; i--) {
      setTimeout(() => {
        setResendCount(i);
      }, (10 - i) * 1000);
    }
  };

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleChange = (value, index) => {
    // Handle full-string paste that arrives via onChange
    if (value && value.length > 1) {
      const chars = value.trim().split("");
      const filtered = chars.filter((c) => /^[A-Za-z0-9]$/.test(c));
      const newOtp = [...otp];
      let filled = 0;
      for (let i = 0; i < filtered.length && index + i < length; i++) {
        newOtp[index + i] = filtered[i];
        filled++;
      }
      setOtp(newOtp);
      if (filled > 0) {
        inputRefs.current[Math.min(index + filled - 1, length - 1)]?.focus();
      }
      return;
    }

    if (/^[A-Za-z0-9]$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handlePaste = async (e, startIndex = 0) => {
    e.preventDefault();
    let text = "";
    try {
      text =
        e.clipboardData?.getData("text/plain") ||
        e.clipboardData?.getData("text") ||
        (e.nativeEvent?.clipboardData && e.nativeEvent.clipboardData.getData("Text")) ||
        (window?.clipboardData && window.clipboardData.getData("Text")) ||
        "";
      if (!text && navigator?.clipboard?.readText) {
        text = await navigator.clipboard.readText();
      }
    } catch (_) {}

    const chars = (text || "").trim().split("");
    const filtered = chars.filter((c) => /^[A-Za-z0-9]$/.test(c)).slice(0, length);
    const newOtp = [...otp];
    let filled = 0;
    for (let i = 0; i < filtered.length && startIndex + i < length; i++) {
      newOtp[startIndex + i] = filtered[i];
      filled++;
    }
    setOtp(newOtp);
    if (filled > 0) {
      inputRefs.current[Math.min(startIndex + filled - 1, length - 1)]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    const key = e.key;
    // Allow common shortcuts like paste (Cmd/Ctrl+V) to bubble so onPaste triggers
    if (e.metaKey || e.ctrlKey) {
      return;
    }
    if (/^[A-Za-z0-9]$/.test(key)) {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[index] = key;
      setOtp(newOtp);
      if (index < length - 1) inputRefs.current[index + 1]?.focus();
      return;
    }
    if (key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (newOtp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
      return;
    }
  };

  const onVerifyOtpClick = async () => {
    const code = otp.join("");
    if (code.length < length) {
      setErrorMessage("Please enter the 6-digit code.");
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await verifyUpdatedEmail({ email: updatedEmail, otp: String(code) });
      const payload = response?.payload;
      if (payload?.success || response?.status === "success") {
        setSuccessMessage("Email verified successfully!");
        sessionStorage.removeItem("updatedEmail");
        if (typeof onVerifyEmail === "function") {
          onVerifyEmail();
        }
      } else if (response?.status === 422) {
        setErrorMessage(response?.message || response?.mesage || "Invalid code. Please try again.");
      } else if (response?.message || response?.mesage) {
        setErrorMessage(response.message || response.mesage);
      } else {
        setErrorMessage("Verification failed. Please try again.");
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.mesage || error?.message;
      const apiStatus = error?.response?.status || error?.response?.data?.status;
      if (apiStatus === 422) {
        setErrorMessage(apiMessage || "Invalid code. Please try again.");
      } else if (apiMessage) {
        setErrorMessage(apiMessage);
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onResendEmail = async () => {
    setIsLoading(true);
    setResendDisabled(true);
    resendFunction();
    try {
      const response = await resendEmail({ email: updatedEmail });
      if (response.status === "success") {
        setSuccessMessage("Email sent successfully!");
      } else if (response?.status === 422) {
        setErrorMessage(response?.message || response?.mesage || "Unable to resend code. Please try again later.");
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.mesage || error?.message;
      setErrorMessage(apiMessage || "Error sending email. Please try again later.");
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setResendDisabled(false);
        setResendCount(10);
      }, 10000);
    }
  };

  return (
    <div className="set-phone-modal-container">
      {isLoading && (
        <div className="loader-df-container">
          <Loader type="spinner-cub" bgColor={"#2D50A7"} color={"#3D6DEB"} size={45} />
        </div>
      )}
      <div>
        <div className="set-phone-modal-head">Email verification</div>
        <div className="set-phone-modal-support">
          Please check your inbox ({updatedEmail}) for the confirmation code.
        </div>
      </div>
      <div className="set-phone-int-cnt" style={{ marginBottom: "10px" }}>
        <div className="otp-verifier">
          <div className="otp-container" onPaste={(e) => handlePaste(e, 0)}>
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={(e) => handlePaste(e, index)}
                inputMode="numeric"
                ref={(el) => (inputRefs.current[index] = el)}
                autoFocus={index === 0}
                autoComplete="one-time-code"
                className="otp-box"
              />
            ))}
          </div>
          {errorMessage && (
            <div style={{ color: "red", textAlign: "center", marginTop: "12px" }}>{errorMessage}</div>
          )}
          <div className="otp-rote">
            <div className="otp-typo-cnt">
              <div className="otp-get-code">Didn’t get a code?</div>
              <div
                className="otp-resend"
                onClick={() => {
                  if (!resendDisabled) onResendEmail();
                }}
                style={{ cursor: resendDisabled ? "not-allowed" : "pointer", opacity: resendDisabled ? 0.6 : 1 }}
              >
                Resend code {resendDisabled ? `(${resendCount}s)` : ""}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "20px 0" }}>
          <button
            className="set-phone-continue-button"
            onClick={onVerifyOtpClick}
            disabled={isLoading}
            type="button"
            style={{ cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.6 : 1 }}
          >
            Continue <img src={arrowRight} alt="edit" />
          </button>
        </div>

        {successMessage && (
          <div style={{ color: "green", textAlign: "center" }}>{successMessage}</div>
        )}
      </div>
    </div>
  );
}

export default EmailSettingsScreen3;